/**
 * Trial Connection Migration Error Handling Service
 * Provides comprehensive error handling, rollback mechanisms, and user feedback
 * for the trial connection migration system
 */

class MigrationErrorHandler {
  constructor() {
    this.retryAttempts = 3;
    this.retryDelay = 2000; // 2 seconds
    this.migrationLog = [];
  }

  /**
   * Handle migration errors with automatic retry and rollback
   * @param {Object} error - The error that occurred
   * @param {Object} context - Migration context (user_id, old_type, new_type, etc.)
   * @param {Function} migrationFunction - The migration function to retry
   * @param {Function} rollbackFunction - Function to rollback changes if all retries fail
   * @returns {Promise<Object>} Migration result with error handling
   */
  async handleMigrationError(error, context, migrationFunction, rollbackFunction) {
    console.error('🚨 Migration error occurred:', {
      error: error.message,
      context,
      timestamp: new Date().toISOString()
    });

    // Log the error
    this.migrationLog.push({
      type: 'error',
      message: error.message,
      context,
      timestamp: new Date().toISOString(),
      stack: error.stack
    });

    // Determine error type and response strategy
    const errorType = this.categorizeError(error);
    
    switch (errorType) {
      case 'NETWORK_ERROR':
        return await this.handleNetworkError(error, context, migrationFunction);
      
      case 'VALIDATION_ERROR':
        return await this.handleValidationError(error, context);
      
      case 'PERMISSION_ERROR':
        return await this.handlePermissionError(error, context);
      
      case 'SYSTEM_ERROR':
        return await this.handleSystemError(error, context, migrationFunction, rollbackFunction);
      
      case 'TIMEOUT_ERROR':
        return await this.handleTimeoutError(error, context, migrationFunction);
      
      default:
        return await this.handleUnknownError(error, context, rollbackFunction);
    }
  }

  /**
   * Categorize error types for appropriate handling
   */
  categorizeError(error) {
    const message = error.message?.toLowerCase() || '';
    const status = error.response?.status;

    if (error.code === 'NETWORK_ERROR' || message.includes('network') || !status) {
      return 'NETWORK_ERROR';
    }
    
    if (status === 400 || message.includes('validation') || message.includes('invalid')) {
      return 'VALIDATION_ERROR';
    }
    
    if (status === 401 || status === 403 || message.includes('unauthorized') || message.includes('forbidden')) {
      return 'PERMISSION_ERROR';
    }
    
    if (status === 408 || message.includes('timeout')) {
      return 'TIMEOUT_ERROR';
    }
    
    if (status >= 500 || message.includes('internal server error')) {
      return 'SYSTEM_ERROR';
    }

    return 'UNKNOWN_ERROR';
  }

  /**
   * Handle network errors with exponential backoff retry
   */
  async handleNetworkError(error, context, migrationFunction) {
    console.log('🔄 Handling network error with retry logic...');
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        console.log(`🔄 Retry attempt ${attempt}/${this.retryAttempts} for migration...`);
        
        // Wait with exponential backoff
        if (attempt > 1) {
          const delay = this.retryDelay * Math.pow(2, attempt - 2);
          await this.sleep(delay);
        }
        
        const result = await migrationFunction();
        
        console.log('✅ Migration succeeded on retry attempt', attempt);
        this.migrationLog.push({
          type: 'success_after_retry',
          attempt,
          context,
          timestamp: new Date().toISOString()
        });
        
        return {
          success: true,
          result,
          recovered: true,
          attempts: attempt,
          message: `Migration completed successfully after ${attempt} attempt(s)`
        };
        
      } catch (retryError) {
        console.log(`❌ Retry attempt ${attempt} failed:`, retryError.message);
        
        if (attempt === this.retryAttempts) {
          // All retries exhausted
          return {
            success: false,
            error: retryError,
            attempts: attempt,
            message: `Migration failed after ${attempt} attempts due to network issues. Please check your connection and try again.`,
            userAction: 'retry_later'
          };
        }
      }
    }
  }

  /**
   * Handle validation errors (non-retryable)
   */
  async handleValidationError(error, context) {
    console.log('⚠️ Handling validation error...');
    
    const userFriendlyMessage = this.getValidationErrorMessage(error, context);
    
    return {
      success: false,
      error,
      attempts: 1,
      message: userFriendlyMessage,
      userAction: 'fix_data',
      requiresUserIntervention: true
    };
  }

  /**
   * Handle permission errors
   */
  async handlePermissionError(error, context) {
    console.log('🔒 Handling permission error...');
    
    return {
      success: false,
      error,
      attempts: 1,
      message: 'Migration failed due to insufficient permissions. Please log out and log back in, then try again.',
      userAction: 'reauth',
      requiresUserIntervention: true
    };
  }

  /**
   * Handle system errors with rollback
   */
  async handleSystemError(error, context, migrationFunction, rollbackFunction) {
    console.log('🛠️ Handling system error with rollback...');
    
    // Try once more before rollback
    try {
      await this.sleep(1000); // Brief pause
      const result = await migrationFunction();
      
      return {
        success: true,
        result,
        recovered: true,
        attempts: 2,
        message: 'Migration completed successfully after system recovery'
      };
    } catch (secondError) {
      // System error persists, initiate rollback
      console.log('🔄 Initiating rollback due to persistent system error...');
      
      try {
        if (rollbackFunction) {
          await rollbackFunction();
          console.log('✅ Rollback completed successfully');
        }
        
        return {
          success: false,
          error: secondError,
          attempts: 2,
          rollbackPerformed: true,
          message: 'Migration failed due to system error. Your original settings have been restored. Please try again later.',
          userAction: 'retry_later'
        };
      } catch (rollbackError) {
        console.error('❌ Rollback failed:', rollbackError);
        
        return {
          success: false,
          error: secondError,
          rollbackError,
          attempts: 2,
          rollbackPerformed: false,
          message: 'Migration failed and automatic recovery was unsuccessful. Please contact support for assistance.',
          userAction: 'contact_support',
          critical: true
        };
      }
    }
  }

  /**
   * Handle timeout errors with retry
   */
  async handleTimeoutError(error, context, migrationFunction) {
    console.log('⏰ Handling timeout error...');
    
    // Single retry for timeout
    try {
      await this.sleep(3000); // Wait longer for timeout recovery
      const result = await migrationFunction();
      
      return {
        success: true,
        result,
        recovered: true,
        attempts: 2,
        message: 'Migration completed successfully after timeout recovery'
      };
    } catch (retryError) {
      return {
        success: false,
        error: retryError,
        attempts: 2,
        message: 'Migration timed out. This may indicate high system load. Please try again in a few minutes.',
        userAction: 'retry_later'
      };
    }
  }

  /**
   * Handle unknown errors with safe fallback
   */
  async handleUnknownError(error, context, rollbackFunction) {
    console.log('❓ Handling unknown error...');
    
    try {
      if (rollbackFunction) {
        await rollbackFunction();
        console.log('✅ Precautionary rollback completed');
      }
      
      return {
        success: false,
        error,
        attempts: 1,
        rollbackPerformed: true,
        message: 'Migration encountered an unexpected error. Your original settings have been preserved. Please contact support if this continues.',
        userAction: 'contact_support'
      };
    } catch (rollbackError) {
      return {
        success: false,
        error,
        rollbackError,
        attempts: 1,
        rollbackPerformed: false,
        message: 'Migration failed with an unexpected error. Please contact support immediately.',
        userAction: 'contact_support',
        critical: true
      };
    }
  }

  /**
   * Get user-friendly validation error messages
   */
  getValidationErrorMessage(error, context) {
    const message = error.message?.toLowerCase() || '';
    
    if (message.includes('user_id')) {
      return 'Migration failed: User identification error. Please log out and log back in.';
    }
    
    if (message.includes('account_type')) {
      return 'Migration failed: Invalid account type specified. Please refresh the page and try again.';
    }
    
    if (message.includes('connection')) {
      return 'Migration failed: Connection data validation error. Please refresh your connections and try again.';
    }
    
    return 'Migration failed due to invalid data. Please refresh the page and try again.';
  }

  /**
   * Create rollback function for billing rollback
   */
  createBillingRollbackFunction(originalSubscription, apiClient, userId) {
    return async () => {
      console.log('🔄 Rolling back billing changes...');
      
      if (!originalSubscription) {
        console.log('⚠️ No original subscription to rollback to');
        return;
      }
      
      try {
        await apiClient.put(`/api/billing/${userId}/subscription`, {
          plan_type: originalSubscription.plan_type,
          billing_cycle: originalSubscription.billing_cycle
        });
        
        console.log('✅ Billing rollback completed');
      } catch (rollbackError) {
        console.error('❌ Billing rollback failed:', rollbackError);
        throw new Error('Failed to rollback billing changes');
      }
    };
  }

  /**
   * Get migration summary for user display
   */
  getMigrationSummary() {
    const errors = this.migrationLog.filter(log => log.type === 'error');
    const successes = this.migrationLog.filter(log => log.type === 'success' || log.type === 'success_after_retry');
    
    return {
      totalOperations: this.migrationLog.length,
      errors: errors.length,
      successes: successes.length,
      logs: this.migrationLog
    };
  }

  /**
   * Utility function for delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear migration logs
   */
  clearLogs() {
    this.migrationLog = [];
  }
}

// Export singleton instance
const migrationErrorHandler = new MigrationErrorHandler();
export default migrationErrorHandler;